import React, { useEffect, useRef, useState } from 'react'
import './challenges-form.styles.scss';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import ManageContentInformation from '../blocks/information.component';
import ManageContentVisibility from '../blocks/visibility.component';
import ManageContentIconCropped from '../blocks/icon.component';
import ManageContentCategories from '../blocks/categories.component';
import ManageContentLimitations from '../blocks/limitations.component';
import ManageContentPrerequisites from '../blocks/prerequisites.component';
import ManageContentDates from '../blocks/dates.component';
import { useSelector } from 'react-redux';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { UseCloudFunction } from '@/utils/firebase.utils';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import ManageContentRewards from '../blocks/rewards.component';
import { RandomString } from '@/utils/randomString';
import { membershipRestrictions, testMemberRestrictions } from '@/utils/membershipRestrictions';
import UpgradeRequiredModal from '@/components/SHARED/upgrade-required-modal/upgrade-required-modal.component';
import FullPageLoader from '@/components/loader/full-page-loader.component';
import ElementDelete from '@/components/SHARED/element-delete/element-delete.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretRight, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import ShowHideSettingsButton from '../blocks/show-hide-button.component';
import useAnalytics from '@/utils/useAnalytics';
import { getOptimizedBlob } from '@/utils/getOptimizedBlob';

const ChallengesForm = ({
    gameElements,
    challengesFromDb,
    gameData,
    playerList
}) => {

    const membership = useSelector(state => state.userData.membership);
    const userProfile = useSelector(state => state.userData.userProfile);
    const screenSize = useSelector(state => state.contentData.screenSize);
    const [formData, setFormData] = useState({})
    const [availableItems, setAvailableItems] = useState(null);
    const [alertMessage, setAlertMessage] = useState(null);
    const [confirmMessage, setConfirmMessage] = useState(null);
    const [existingDescription, setExistingDescription] = useState(null);
    const [clearEditor, setClearEditor] = useState(false);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(null);
    const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
    const { challengePath } = useParams();
    const { trackEvent, trackException } = useAnalytics();
    const dataRef = useRef();
    const approveRef = useRef();
    const navigate = useNavigate();
    const storage = getStorage();
    const location = useLocation();

    useEffect(() => {
        if (!gameData || !membership || !location) return;
        if (location.pathname.includes('/elements/challenges/edit')) return;
        const res = testMemberRestrictions({ 'gameData': gameData, 'membership': membership, 'create': true });
        if (res.disabled && res.disabledReason.includes('elements')) {
            const planRest = membershipRestrictions[membership.membership];
            const message = `While using the ${planRest.title}, you are only able to have ${planRest.elementCount} active Game Elements per game.`;
            setShowUpgradeModal(message);
            return;
        }
    }, [gameData, membership, location])

    useEffect(() => {
        if (!challengePath || !challengesFromDb) {
            dataRef.current = {
                'opt_visible': true,
                'opt_available': true,
                'opt_preview': true,
            }
            setFormData(dataRef.current);
            return;
        };
        dataRef.current = Object.values(challengesFromDb).filter(e => e.path === challengePath)[0];
        setFormData(dataRef.current);
        if (dataRef.current.desc) {
            setExistingDescription(dataRef.current.desc);
        }
    }, [challengePath, challengesFromDb])

    useEffect(() => {
        if (!challengesFromDb) { return; }
        let arr = [];
        for (let c of Object.values(challengesFromDb).filter(c => c.type === 'challenge')) {
            if (c.opt_cat && !arr.includes(c.opt_cat)) {
                arr.push(c.opt_cat)
            }
        }
        setCategories(arr.sort())
    }, [challengesFromDb])

    useEffect(() => {
        if (!gameElements) return;
        console.log(gameElements);
        let items = Object.values(gameElements).filter(e =>
            e.type === 'item' && 
            e.opt_itemType === 'inventory' &&
            e.status === 'active'
        );
        console.log(items);
        setAvailableItems(items);
    }, [gameElements])

    function saveData(key, value) {
        dataRef.current = {
            ...dataRef.current,
            ...{ [key]: value }
        };
        setFormData(dataRef.current);
    }

    function cancelCreate() {
        setConfirmMessage('Are you sure you want to cancel? All of your changed data will be lost.');
        approveRef.current = () => {
            dataRef.current = {};
            setFormData(dataRef.current);
            navigate(-1)
        }
    }

    async function handleSubmit() {
        if (location.pathname.includes('/preview/manager')) {
            setAlertMessage('Sorry, data cannot be saved in preview mode!');
            return;
        }
        let tempData = { ...dataRef.current }
        const reqs = ['name', 'icon', 'desc']
        for (let r of reqs) {
            if (!tempData[r]) {
                setAlertMessage('Please include a name, an icon, and a description for this Challenge before saving.');
                return;
            }
        }
        if (
            gameElements &&
            Object.values(gameElements).filter(e =>
                e.name.trim().toLowerCase() === tempData.name.trim().toLowerCase() &&
                e.id !== tempData.id
            ).length > 0
        ) {
            setAlertMessage('There is already a Challenge with this name. ' +
                'Please choose a different name for this challenge.');
            return;
        }
        setConfirmMessage('Are you sure you are ready to save this Challenge? All of these settings can be changed later.')
        approveRef.current = () => {
            finishApproval()
        }
    }

    async function finishApproval() {
        setLoading(true);
        let tempData = { ...dataRef.current }
        if (tempData.icon.upload) {
            // tempData.icon.croppie.result({
            // 	type:'blob',
            // 	size: {'width': 352, 'height': 352},
            //     quality: 0.7,
            // })
            // .then((blob) => {
            // 	uploadImage(tempData.icon, blob)
            // 	return;
            // })
            const optimizedBlob = await getOptimizedBlob(tempData.icon.croppie, 25);
            console.log('Cropped image size in KB:', optimizedBlob.size / 1024);
            uploadImage(tempData.icon, optimizedBlob);
        } else if (tempData.icon.url) {
            saveChallenge(tempData.icon.url)
            return;
        } else if (tempData.icon) {
            saveChallenge(tempData.icon)
            return;
        } else {
            setAlertMessage('Something went wrong with the icon. Please try using a different image.');
            setLoading(false);
            return;
        }
    }

    function uploadImage(icon, blob) {
        const now = new Date().getTime();
        const path = `${userProfile.appId}/${now}-${icon.name}`;
        const iconRef = ref(storage, path);
        const metadata = {
            contentType: icon.type,
            customMetadata: {
                appId: userProfile.appId,
                email: userProfile.email
            }
        };
        uploadBytes(iconRef, blob, metadata).then((snapshot) => {
            getDownloadURL(iconRef)
                .then(async (url) => {
                    saveChallenge(url);
                    try {
                        await UseCloudFunction(
                            'saveToMyContent',
                            {
                                'gameId': gameData.gameId,
                                'appId': userProfile.appId,
                                'source': 'Challenge Icons',
                                'category': 'Images',
                                'cropped': true,
                                'contentData': {
                                    'name': icon.name,
                                    'url': url,
                                    'type': 'image',
                                    'id': `${now}_${RandomString(10)}_image`
                                },
                            }
                        )
                    } catch (error) {
                        console.log('Error saving to My Content: ', error);
                        trackException('Challenge Icon Save to My Content Error: ' + error.message);
                    }
                    trackEvent(
                        'image_saved_to_my_content',
                        'Game Manager',
                        `Challenge icon ${icon.name} was uploaded for user ${userProfile.appId}.`,
                        null,
                        membership
                    )
                    return;
                })
        })
    }

    async function saveChallenge(iconUrl) {
        let tempData = { ...dataRef.current }
        console.log(tempData);
        const now = new Date().getTime();
        const gameCode = gameData.gameId.substring(gameData.gameId.indexOf('-') - 5, gameData.gameId.indexOf('-') + 6)
        let action = 'Edited';
        if (!tempData.id) {
            tempData.appId = userProfile.appId;
            tempData.gameId = gameData.gameId;
            tempData.id = now + '-' + gameCode + '-challenge';
            tempData.status = 'active';
            tempData.type = 'challenge';
            tempData.path = RandomString(18, 'abcdefghijkmnopqrstuvwxyz-_023456789');
            tempData.public = true;
            tempData.opt_available = true;
            action = 'Created';
        }
        if (!tempData.opt_dateOpen) {
            tempData.opt_dateOpen = now;
        }
        tempData.icon = iconUrl;
        console.log(tempData);
        const res = await UseCloudFunction(
            'saveGameElement',
            {
                'gameId': gameData.gameId,
                'appId': userProfile.appId,
                'elementData': tempData,
                'userName': `${userProfile.fName} ${userProfile.lName}`,
                'action': action
            }
        )
        console.log(res);
        if (res.error) {
            setAlertMessage('Something went wrong. Please try again later. Error: ' + res.error);
            setLoading(false);
            trackException(`Challenge ${action === 'Created' ? 'creation' : 'edit'} Error: ${res.error}`);
            return;
        }
        setAlertMessage('Challenge saved successfully.');
        dataRef.current = {};
        setFormData(dataRef.current);
        setLoading(false);
        const event = action === 'Created' ? 'challenge_created' : 'challenge_edited';
        trackEvent(
            event,
            'Game Manager',
            `Challenge ${tempData.id} was ${action === 'Created' ? 'created' : 'edited'}.`,
            null,
            membership
        )
        navigate(-1);
    }

    return (
        <div className={`create-challenge-form ${screenSize}`}>
            <span className='required meta'>* = Required</span>
            <div className="g-space-0-5"></div>
            <ManageContentInformation
                type='challenge'
                saveData={saveData}
                formData={formData}
                existingDescription={existingDescription}
                clearEditor={clearEditor}
                setClearEditor={setClearEditor}
                textEditorLabel='Challenge Instructions (optional)'
            />
            <div className='g-space-2'></div>
            <ManageContentIconCropped
                type='challenge'
                saveData={saveData}
                formData={formData}
            />
            <div className='g-space-1'></div>
            <ShowHideSettingsButton
                showAdvancedOptions={showAdvancedOptions}
                setShowAdvancedOptions={setShowAdvancedOptions} />
            <div className='g-space-1'></div>
            <div className={`aditional-options ${showAdvancedOptions ? 'show' : 'hide'}`}>
                <ManageContentCategories
                    type='challenge'
                    saveData={saveData}
                    formData={formData}
                    categories={categories}
                />
                <div className='g-space-2'></div>
                <ManageContentVisibility
                    type='challenge'
                    saveData={saveData}
                    formData={formData}
                    previewText="Allow players to see this challenge before they are allowed submit a response. If toggled off, players will see a mystery challenge and will not be able to access the challenge's information."
                    visibilityText="Turn visibility ON for this Challenge. If toggled off, it will be totally hidden from players."
                    availabilityText="This Challenge is able to be accessed and completed by players if it is open according to the dates below and if the player meets any chosen prerequisites. If toggled off, players cannot submit content for this challenge."
                />
                <div className='g-space-2'></div>
                <ManageContentLimitations
                    type='challenge'
                    saveData={saveData}
                    formData={formData}
                />
                <div className='g-space-2'></div>
                <ManageContentPrerequisites
                    type='challenge'
                    saveData={saveData}
                    formData={formData}
                    gameElements={gameElements}
                    availableItems={availableItems}
                    teams={
                        Object.values(playerList)
                            .filter(p => p.team && p.status === 'active')
                            .sort((a, b) => a.displayName.toLowerCase() > b.displayName.toLowerCase() ? 1 : -1)
                    }
                    gameData={gameData}
                />
                <div className='g-space-2'></div>
                <ManageContentRewards
                    type='challenge'
                    saveData={saveData}
                    formData={formData}
                    gameElements={gameElements}
                    text='completing this challenge'
                    gameData={gameData}
                />
                <div className='g-space-2'></div>
                <ManageContentDates
                    type='challenge'
                    saveData={saveData}
                    formData={formData}
                    gameElements={gameElements}
                    text='Challenges will appear, but they will not be able to be completed before or after these dates.'
                />
                <div className='g-space-1'></div>
                <ShowHideSettingsButton
                    showAdvancedOptions={showAdvancedOptions}
                    setShowAdvancedOptions={setShowAdvancedOptions}
                    below={true} />
            </div>
            <div className='buttons'>
                {
                    (loading)
                        ?
                        <>
                            {
                                (formData.ts_created) &&
                                <button
                                    type='button'
                                    className={`g-button small-btn red ${screenSize !== 'mobile' ? 'left-item' : ''}`}>
                                    Delete
                                </button>
                            }
                            <button
                                type='button'
                                className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}>
                                Cancel
                            </button>
                            <button
                                type='button'
                                className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}>
                                <ThreeDotsLoader />
                            </button>
                        </>
                        :
                        <>
                            {
                                (formData.ts_created) &&
                                <ElementDelete
                                    element={formData}
                                    style='button'
                                    classText={screenSize !== 'mobile' ? 'left-item' : ''} />
                            }
                            <button
                                type='button'
                                className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}
                                onClick={() => cancelCreate()}>
                                Cancel
                            </button>
                            <button
                                type='button'
                                className={`g-button submit-button`}
                                onClick={() => handleSubmit()}>
                                Save Challenge
                            </button>
                        </>
                }
            </div>
            <ModalAlert show={alertMessage} cancel={() => setAlertMessage(null)} message={alertMessage} />
            <ModalConfirm show={confirmMessage} cancel={() => setConfirmMessage(null)} message={confirmMessage} onApprove={approveRef.current} />
            <UpgradeRequiredModal
                show={showUpgradeModal}
                cancel={() => setShowUpgradeModal(null)}
                message={showUpgradeModal}
            />
            <FullPageLoader show={loading} />
        </div>
    )
}

export default ChallengesForm